// Exponential moving average smoother with outlier rejection.
//
// CdA values are noisy frame-to-frame because (a) the segmentation mask
// wiggles by a few pixels, (b) the rider's limbs move during pedalling,
// (c) calibration confidence fluctuates. An EMA (α ≈ 0.15) smooths this
// out without introducing significant lag. Outliers — points more than
// 4 standard deviations from the running mean — are rejected so a bad
// single-frame reading doesn't pollute the smoothed value.

/**
 * Median filter — ring buffer of N values, returns the median. Kills
 * single-frame outliers while preserving genuine step changes.
 */
export class MedianFilter {
  private readonly size: number;
  private readonly buffer: number[] = [];

  constructor(size: number = 5) {
    this.size = size;
  }

  update(raw: number): number {
    this.buffer.push(raw);
    if (this.buffer.length > this.size) this.buffer.shift();
    const sorted = [...this.buffer].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
  }

  reset() {
    this.buffer.length = 0;
  }
}

export class EmaSmoother {
  private value: number | null = null;
  private variance = 0;
  private readonly alpha: number;
  private readonly outlierSigma: number;

  constructor(alpha: number = 0.15, outlierSigma: number = 4) {
    this.alpha = alpha;
    this.outlierSigma = outlierSigma;
  }

  /** Feed a raw reading; returns the smoothed value. Returns null until first reading. */
  update(raw: number): number | null {
    if (!Number.isFinite(raw) || raw <= 0) return this.value;

    if (this.value === null) {
      this.value = raw;
      this.variance = 0;
      return this.value;
    }

    // Outlier check (skip for the first few samples while variance settles)
    const std = Math.sqrt(this.variance);
    if (std > 0 && Math.abs(raw - this.value) > this.outlierSigma * std) {
      // Outlier — pull toward the running value by 1/4 instead of α
      this.value = this.value * 0.75 + raw * 0.25;
      return this.value;
    }

    // Standard EMA update
    const next = this.value * (1 - this.alpha) + raw * this.alpha;
    // Update variance estimate (Welford-ish)
    const delta = raw - this.value;
    this.variance = this.variance * (1 - this.alpha) + delta * delta * this.alpha;
    this.value = next;
    return this.value;
  }

  get current(): number | null {
    return this.value;
  }

  reset() {
    this.value = null;
    this.variance = 0;
  }
}
